// Central app-state store: loads songs/setlists/settings from IndexedDB into
// memory once, exposes mutation methods that write through to IndexedDB, and
// notifies subscribers. Kept deliberately simple (no external state library) —
// this is a small, well-understood domain.

import { generateId } from './id';
import { database } from '../db/database';
import { inspectWavFile } from './wavInspect';
import { detectAudioFormat, probeDurationSeconds } from './audioFormat';
import { createZip, readZip } from './zip';
import type { CloudSongRecord } from './songSync';
import { addSyncDiagnostic } from './syncDiagnostics';
import {
  DEFAULT_MARKER_LEAD_IN_SECONDS,
  DEFAULT_METRONOME_SETTINGS,
  DEFAULT_REHEARSAL_ROUTING,
  DEFAULT_SETTINGS,
  EMPTY_MARKER_NOTE_FORMATS,
  EMPTY_MARKER_NOTE_SHEETS,
  EMPTY_MARKER_NOTES,
  migrateSetlistCountIn,
  migrateSetlistRouting,
  type AppSettings,
  type AudioFileFormat,
  type BackupFileV1,
  type BandRecord,
  type CustomNoteTab,
  type SetlistCountInOverride,
  type SetlistMode,
  type SetlistRecord,
  type SetlistRouting,
  type SongMarker,
  type SongRecord,
  type SongStatus,
} from './types';

interface CloudBandMetadata {
  id: string;
  name: string;
}

function normalizedSongName(name: string): string {
  return name.trim().toLowerCase();
}

function betterCloudSong(a: CloudSongRecord, b: CloudSongRecord): CloudSongRecord {
  if (b.markers.length !== a.markers.length) return b.markers.length > a.markers.length ? b : a;
  if ((b.durationSeconds ?? 0) !== (a.durationSeconds ?? 0)) return (b.durationSeconds ?? 0) > (a.durationSeconds ?? 0) ? b : a;
  return a;
}

function backfillMarker(marker: SongMarker): SongMarker {
  return {
    ...marker,
    shortLabel: marker.shortLabel ?? '',
    notes: { ...EMPTY_MARKER_NOTES, ...marker.notes },
    noteFormats: { ...EMPTY_MARKER_NOTE_FORMATS, ...marker.noteFormats },
    noteSheets: { ...EMPTY_MARKER_NOTE_SHEETS, ...marker.noteSheets },
  };
}

export interface AudioFileInspection {
  fileFormat: AudioFileFormat | null;
  status: SongStatus;
  statusMessage: string | null;
  durationSeconds: number | null;
  channelCount: number | null;
}

/** Migrates a song record from before `firstBeatTimeSeconds` existed (when
 * pickup was a hand-typed beat COUNT, `pickupBeats`) into an equivalent
 * timestamp — the inverse of the count derivation: reconstruct
 * approximately where that many beats past the click's own offset would
 * land, using the song's current BPM/offset. Best-effort only (the whole
 * point of switching to a marked timestamp is that it stays correct even if
 * BPM/offset are retuned later, which a reconstructed value from the OLD
 * settings obviously can't guarantee) — but it preserves the user's prior
 * pickup-beat tuning across the upgrade rather than silently resetting it
 * to "not marked." Returns null (not marked) if there's no legacy value to
 * migrate, matching the field's own "no pickup" default. */
function legacyPickupBeatsToFirstBeatTime(song: SongRecord & { pickupBeats?: number }): number | null {
  const legacyPickupBeats = song.pickupBeats;
  if (!legacyPickupBeats || legacyPickupBeats <= 0) return null;
  const secondsPerBeat = 60 / song.metronomeSettings.bpm;
  return song.syntheticClickOffsetSeconds + legacyPickupBeats * secondsPerBeat;
}

/**
 * Inspects an imported/replaced audio file and classifies it. WAV files get a
 * hard channel-count check (see wavInspect.ts). MP3 files are accepted as
 * plain 'ok' — no channel-separation warning (explicit product decision:
 * plenty of people import an MP3 purely to play/practice along with no
 * click track at all, for whom that caveat was pure noise every time they
 * glanced at their library).
 */
export async function inspectAudioFile(file: Blob): Promise<AudioFileInspection> {
  const fileFormat = await detectAudioFormat(file);

  if (fileFormat === 'mp3') {
    return {
      fileFormat,
      status: 'ok',
      statusMessage: null,
      durationSeconds: await probeDurationSeconds(file),
      channelCount: null,
    };
  }

  if (fileFormat === 'wav') {
    const inspection = await inspectWavFile(file);
    if (!inspection.isValidWav) {
      return {
        fileFormat,
        status: 'unsupported',
        statusMessage: inspection.error ?? 'Unsupported audio file.',
        durationSeconds: null,
        channelCount: null,
      };
    }
    if (inspection.channelCount === 1) {
      return {
        fileFormat,
        status: 'mono-warning',
        statusMessage: 'This file is mono, not stereo. The click/cue channel will be silent.',
        durationSeconds: inspection.durationSeconds,
        channelCount: inspection.channelCount,
      };
    }
    return {
      fileFormat,
      status: 'ok',
      statusMessage: null,
      durationSeconds: inspection.durationSeconds,
      channelCount: inspection.channelCount,
    };
  }

  return {
    fileFormat: null,
    status: 'unsupported',
    statusMessage: 'This file is not a recognized WAV or MP3 audio file.',
    durationSeconds: null,
    channelCount: null,
  };
}

export interface AppState {
  songs: SongRecord[];
  setlists: SetlistRecord[];
  bands: BandRecord[];
  settings: AppSettings;
  loaded: boolean;
}

type Listener = () => void;

export class AppStore {
  private state: AppState = { songs: [], setlists: [], bands: [], settings: DEFAULT_SETTINGS, loaded: false };
  private listeners = new Set<Listener>();
  /** Every mutating method reads `this.state`, computes a new value, then
   * writes IndexedDB and reassigns `this.state` — with no locking, two
   * overlapping calls (e.g. two "add a marker" clicks in quick succession)
   * can race: the second call's read happens before the first call's write,
   * so the first call's change is silently clobbered when the second
   * finishes. Routing every mutation through this queue makes them run
   * strictly one at a time, in call order, regardless of how their
   * internal `await`s (IndexedDB writes) interleave. */
  private writeQueue: Promise<unknown> = Promise.resolve();

  private enqueue<T>(fn: () => Promise<T>): Promise<T> {
    const result = this.writeQueue.then(fn, fn);
    // Swallow rejections here so one failed mutation doesn't permanently
    // wedge the queue for every mutation after it; the caller still sees
    // the real rejection via `result`.
    this.writeQueue = result.catch(() => {});
    return result;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }

  getState(): AppState {
    return this.state;
  }

  async load(): Promise<void> {
    const [songs, setlists, bands, activeSetlistId, activeEntryIndex, customNoteTabs] = await Promise.all([
      database.getAllSongs(),
      database.getAllSetlists(),
      database.getAllBands(),
      database.getSetting('activeSetlistId'),
      database.getSetting('activeEntryIndex'),
      database.getSetting('customNoteTabs'),
    ]);
    this.state = {
      // Songs created before markers (or the synthetic click feature)
      // existed won't have these fields in IndexedDB.
      songs: songs.map((s) => ({
        ...s,
        bandId: s.bandId ?? null,
        markers: (s.markers ?? []).map((m) => backfillMarker(m)),
        syntheticClickEnabled: s.syntheticClickEnabled ?? false,
        syntheticClickOffsetSeconds: s.syntheticClickOffsetSeconds ?? 0,
        firstBeatTimeSeconds: s.firstBeatTimeSeconds ?? legacyPickupBeatsToFirstBeatTime(s),
        beatOneReferenceSeconds: s.beatOneReferenceSeconds ?? null,
        // Songs created before the lead-in became a per-song field (it used
        // to be a single global AppSettings value) won't have these either.
        markerLeadInSeconds: s.markerLeadInSeconds ?? DEFAULT_MARKER_LEAD_IN_SECONDS,
        markerLeadInBeats: s.markerLeadInBeats ?? null,
        supabaseSongId: s.supabaseSongId ?? null,
      })),
      // Setlists created before Rehearsal/Live modes (or setlist-level
      // routing) existed won't have these fields in IndexedDB — 'rehearsal'
      // is the safer/current-equivalent behavior (fully interactive), not
      // 'live'. migrateSetlistRouting also converts the three old fixed
      // rehearsal literals (from before the free-form 3×3 picker) into
      // their RehearsalRouting equivalent, and falls back to the safest
      // default (untouched stereo track + stereo click) for anything else
      // missing/unrecognized.
      setlists: setlists.map((s) => ({
        ...s,
        mode: s.mode ?? 'rehearsal',
        routing: migrateSetlistRouting(s.routing),
        countInOverride: migrateSetlistCountIn(s.countInOverride),
      })),
      bands,
      settings: {
        activeSetlistId: activeSetlistId ?? null,
        activeEntryIndex: activeEntryIndex ?? 0,
        outputVolume: 1,
        customNoteTabs: customNoteTabs ?? [],
      },
      loaded: true,
    };
    this.notify();
    // Re-validate stored audio in the background so stale "ok" statuses from
    // a previous session are corrected without blocking initial render.
    void this.revalidateAllSongs();
  }

  private async revalidateAllSongs(): Promise<void> {
    for (const song of this.state.songs) {
      if (song.mode === 'metronome') continue;
      const record = await database.getAudioBlob(song.id);
      if (!record) {
        await this.setSongStatus(song.id, 'missing', 'Audio file not found in local storage. Re-import it.');
        continue;
      }
      try {
        const inspection = await inspectAudioFile(record.blob);
        await this.setSongStatus(song.id, inspection.status, inspection.statusMessage);
      } catch {
        await this.setSongStatus(song.id, 'unreadable', 'The stored audio file could not be read.');
      }
    }
  }

  /** Not queued: only ever called from `revalidateAllSongs`'s background
   * loop, which runs once at load() time before any UI-triggered mutation
   * can plausibly be in flight, so it doesn't need the write queue's
   * protection against overlapping calls. */
  private async setSongStatus(songId: string, status: SongStatus, message: string | null): Promise<void> {
    const song = this.state.songs.find((s) => s.id === songId);
    if (!song || (song.status === status && song.statusMessage === message)) return;
    const updated: SongRecord = { ...song, status, statusMessage: message };
    await database.putSong(updated);
    this.state = { ...this.state, songs: this.state.songs.map((s) => (s.id === songId ? updated : s)) };
    this.notify();
  }

  // ---- Songs ----

  async importSongs(files: File[]): Promise<{ imported: SongRecord[]; duplicateNames: string[] }> {
    return this.enqueue(() => this.importSongsCore(files));
  }

  private async importSongsCore(files: File[]): Promise<{ imported: SongRecord[]; duplicateNames: string[] }> {
    const imported: SongRecord[] = [];
    const duplicateNames: string[] = [];
    const existingNames = new Set(this.state.songs.map((s) => s.name.trim().toLowerCase()));

    for (const file of files) {
      const baseName = file.name.replace(/\.[^/.]+$/, '');
      if (existingNames.has(baseName.trim().toLowerCase())) {
        duplicateNames.push(baseName);
      }
      existingNames.add(baseName.trim().toLowerCase());

      const inspection = await inspectAudioFile(file);

      const now = Date.now();
      const song: SongRecord = {
        id: generateId(),
        name: baseName,
        createdAt: now,
        updatedAt: now,
        mode: 'tracks-click',
        bandId: null,
        originalFileName: file.name,
        fileFormat: inspection.fileFormat,
        durationSeconds: inspection.durationSeconds,
        channelCount: inspection.channelCount,
        fileSizeBytes: file.size,
        status: inspection.status,
        statusMessage: inspection.statusMessage,
        metronomeSettings: { ...DEFAULT_METRONOME_SETTINGS },
        markers: [],
        syntheticClickEnabled: false,
        syntheticClickOffsetSeconds: 0,
        firstBeatTimeSeconds: null,
        beatOneReferenceSeconds: null,
        markerLeadInSeconds: DEFAULT_MARKER_LEAD_IN_SECONDS,
        markerLeadInBeats: null,
        supabaseSongId: null,
      };

      await database.putSong(song);
      await database.putAudioBlob({
        songId: song.id,
        blob: file,
        mimeType: file.type || (inspection.fileFormat === 'mp3' ? 'audio/mpeg' : 'audio/wav'),
      });
      imported.push(song);
    }

    this.state = { ...this.state, songs: [...this.state.songs, ...imported] };
    this.notify();
    return { imported, duplicateNames };
  }

  async createMetronomeSong(name: string): Promise<SongRecord> {
    return this.enqueue(async () => {
      const now = Date.now();
      const song: SongRecord = {
        id: generateId(),
        name,
        createdAt: now,
        updatedAt: now,
        mode: 'metronome',
        bandId: null,
        originalFileName: null,
        fileFormat: null,
        durationSeconds: null,
        channelCount: null,
        fileSizeBytes: null,
        status: 'ok',
        statusMessage: null,
        metronomeSettings: { ...DEFAULT_METRONOME_SETTINGS },
        markers: [],
        syntheticClickEnabled: false,
        syntheticClickOffsetSeconds: 0,
        firstBeatTimeSeconds: null,
        beatOneReferenceSeconds: null,
        markerLeadInSeconds: DEFAULT_MARKER_LEAD_IN_SECONDS,
        markerLeadInBeats: null,
        supabaseSongId: null,
      };
      await database.putSong(song);
      this.state = { ...this.state, songs: [...this.state.songs, song] };
      this.notify();
      return song;
    });
  }

  async renameSong(songId: string, newName: string): Promise<void> {
    return this.enqueue(() => this.updateSongCore(songId, { name: newName }));
  }

  async updateSong(songId: string, patch: Partial<SongRecord>): Promise<void> {
    return this.enqueue(() => this.updateSongCore(songId, patch));
  }

  /** Not queued directly: this is the shared read-modify-write core that
   * `updateSong`, `renameSong`, and the marker methods below all call —
   * each of THEM is queued at their own public entry point, so this core
   * must stay raw or calling e.g. addMarker (queued) -> updateSongCore ->
   * enqueue would deadlock waiting on the very call it's nested inside. */
  private async updateSongCore(songId: string, patch: Partial<SongRecord>): Promise<void> {
    const song = this.state.songs.find((s) => s.id === songId);
    if (!song) return;
    const updated: SongRecord = { ...song, ...patch, updatedAt: Date.now() };
    await database.putSong(updated);
    this.state = { ...this.state, songs: this.state.songs.map((s) => (s.id === songId ? updated : s)) };
    this.notify();
  }

  async upsertCloudLibraryMetadata(bands: CloudBandMetadata[], cloudSongs: CloudSongRecord[]): Promise<{ addedSongs: number; updatedSongs: number; createdSetlist: boolean; duplicateCloudSongs: number; remappedSetlistEntries: number }> {
    return this.enqueue(async () => {
      const now = Date.now();
      for (const band of bands) {
        await database.putBand({ id: band.id, name: band.name, createdAt: now });
      }

      const cloudByName = new Map<string, CloudSongRecord>();
      let duplicateCloudSongs = 0;
      for (const cloudSong of cloudSongs) {
        const key = normalizedSongName(cloudSong.name);
        const current = cloudByName.get(key);
        if (current) duplicateCloudSongs += 1;
        cloudByName.set(key, current ? betterCloudSong(current, cloudSong) : cloudSong);
      }
      const canonicalCloudSongs = [...cloudByName.values()];

      let addedSongs = 0;
      let updatedSongs = 0;
      const localByCloudId = new Map(this.state.songs.filter((song) => song.supabaseSongId).map((song) => [song.supabaseSongId, song]));
      const unlinkedLocalByName = new Map<string, SongRecord>();
      for (const song of this.state.songs) {
        if (!song.supabaseSongId) unlinkedLocalByName.set(normalizedSongName(song.name), song);
      }
      const nextSongs = [...this.state.songs];
      const localIdsByCloudSongId = new Map<string, string>();
      const canonicalLocalIdByName = new Map<string, string>();

      for (const cloudSong of canonicalCloudSongs) {
        const existing = localByCloudId.get(cloudSong.id) ?? unlinkedLocalByName.get(normalizedSongName(cloudSong.name));
        const oldMarkersById = new Map((existing?.markers ?? []).map((marker) => [marker.id, marker]));
        const markers = cloudSong.markers.map((marker) => {
          const oldMarker = oldMarkersById.get(marker.id);
          return backfillMarker({
            ...marker,
            notes: oldMarker?.notes ?? marker.notes,
            noteFormats: oldMarker?.noteFormats ?? marker.noteFormats,
            noteSheets: oldMarker?.noteSheets ?? marker.noteSheets,
          });
        });
        const base: SongRecord = {
          id: existing?.id ?? generateId(),
          name: cloudSong.name,
          createdAt: existing?.createdAt ?? now,
          updatedAt: now,
          mode: cloudSong.mode,
          bandId: cloudSong.bandId,
          originalFileName: cloudSong.originalFileName,
          fileFormat: cloudSong.fileFormat,
          durationSeconds: cloudSong.durationSeconds,
          channelCount: cloudSong.channelCount,
          fileSizeBytes: cloudSong.fileSizeBytes,
          status: existing?.status ?? (cloudSong.mode === 'metronome' ? 'ok' : 'missing'),
          statusMessage: existing?.statusMessage ?? (cloudSong.mode === 'metronome' ? null : 'Audio is not on this device yet. Re-import the file to play it.'),
          metronomeSettings: cloudSong.metronomeSettings,
          markers,
          syntheticClickEnabled: cloudSong.syntheticClickEnabled,
          syntheticClickOffsetSeconds: cloudSong.syntheticClickOffsetSeconds,
          firstBeatTimeSeconds: cloudSong.firstBeatTimeSeconds,
          beatOneReferenceSeconds: cloudSong.beatOneReferenceSeconds,
          markerLeadInSeconds: cloudSong.markerLeadInSeconds,
          markerLeadInBeats: cloudSong.markerLeadInBeats,
          supabaseSongId: cloudSong.id,
        };

        await database.putSong(base);
        localIdsByCloudSongId.set(cloudSong.id, base.id);
        canonicalLocalIdByName.set(normalizedSongName(base.name), base.id);
        if (existing) {
          const index = nextSongs.findIndex((song) => song.id === existing.id);
          if (index >= 0) nextSongs[index] = base;
          updatedSongs += 1;
        } else {
          nextSongs.push(base);
          addedSongs += 1;
        }
      }

      const songIdRemap = new Map<string, string>();
      for (const song of nextSongs) {
        const canonicalId = canonicalLocalIdByName.get(normalizedSongName(song.name));
        if (canonicalId && song.id !== canonicalId) songIdRemap.set(song.id, canonicalId);
      }

      let nextSetlists = this.state.setlists;
      let createdSetlist = false;
      let remappedSetlistEntries = 0;
      if (this.state.setlists.length === 0 && canonicalCloudSongs.length > 0) {
        const firstBand = bands.find((band) => band.id === canonicalCloudSongs[0]?.bandId);
        const setlist: SetlistRecord = {
          id: generateId(),
          name: firstBand ? `${firstBand.name} Cloud Songs` : 'Cloud Songs',
          createdAt: now,
          updatedAt: now,
          entries: canonicalCloudSongs
            .map((song) => localIdsByCloudSongId.get(song.id))
            .filter((songId): songId is string => Boolean(songId))
            .map((songId) => ({ songId })),
          mode: 'rehearsal',
          routing: DEFAULT_REHEARSAL_ROUTING,
          countInOverride: null,
        };
        await database.putSetlist(setlist);
        await database.setSetting('activeSetlistId', setlist.id);
        await database.setSetting('activeEntryIndex', 0);
        nextSetlists = [setlist];
        createdSetlist = true;
      } else if (songIdRemap.size > 0) {
        nextSetlists = this.state.setlists.map((setlist) => {
          let changed = false;
          const entries = setlist.entries.map((entry) => {
            const songId = songIdRemap.get(entry.songId);
            if (!songId) return entry;
            changed = true;
            remappedSetlistEntries += 1;
            return { songId };
          });
          return changed ? { ...setlist, entries, updatedAt: now } : setlist;
        });
        await Promise.all(nextSetlists.map((setlist) => database.putSetlist(setlist)));
      }

      if (duplicateCloudSongs > 0 || remappedSetlistEntries > 0) {
        addSyncDiagnostic({
          level: 'info',
          source: 'cloud-library-merge',
          message: 'Collapsed duplicate cloud/local songs by name for local playback and notes.',
          details: {
            duplicateCloudSongs,
            remappedSetlistEntries,
            canonicalNames: [...canonicalLocalIdByName.keys()].join(', '),
            songIdRemap: [...songIdRemap.entries()].map(([from, to]) => `${from}->${to}`).join(', '),
          },
        });
      }

      const bandById = new Map(this.state.bands.map((band) => [band.id, band]));
      for (const band of bands) bandById.set(band.id, { id: band.id, name: band.name, createdAt: bandById.get(band.id)?.createdAt ?? now });

      this.state = {
        ...this.state,
        bands: [...bandById.values()],
        songs: nextSongs,
        setlists: nextSetlists,
        settings: createdSetlist ? { ...this.state.settings, activeSetlistId: nextSetlists[0]?.id ?? null, activeEntryIndex: 0 } : this.state.settings,
      };
      this.notify();
      return { addedSongs, updatedSongs, createdSetlist, duplicateCloudSongs, remappedSetlistEntries };
    });
  }

  // ---- Song markers ----

  async addMarker(songId: string, timeSeconds: number, label: string): Promise<SongMarker | null> {
    return this.enqueue(async () => {
      const song = this.state.songs.find((s) => s.id === songId);
      if (!song) return null;
      const marker: SongMarker = {
        id: generateId(),
        label,
        shortLabel: '',
        timeSeconds,
        notes: { ...EMPTY_MARKER_NOTES },
        noteFormats: { ...EMPTY_MARKER_NOTE_FORMATS },
        noteSheets: { ...EMPTY_MARKER_NOTE_SHEETS },
      };
      const markers = [...song.markers, marker].sort((a, b) => a.timeSeconds - b.timeSeconds);
      await this.updateSongCore(songId, { markers });
      return marker;
    });
  }

  async updateMarker(songId: string, markerId: string, patch: Partial<Omit<SongMarker, 'id'>>): Promise<void> {
    return this.enqueue(async () => {
      const song = this.state.songs.find((s) => s.id === songId);
      if (!song) return;
      const markers = song.markers
        .map((m) => (m.id === markerId ? { ...m, ...patch } : m))
        .sort((a, b) => a.timeSeconds - b.timeSeconds);
      await this.updateSongCore(songId, { markers });
    });
  }

  async deleteMarker(songId: string, markerId: string): Promise<void> {
    return this.enqueue(async () => {
      const song = this.state.songs.find((s) => s.id === songId);
      if (!song) return;
      await this.updateSongCore(songId, { markers: song.markers.filter((m) => m.id !== markerId) });
    });
  }

  /** Returns true when replacing this song's file with an MP3 forced its
   * synthetic click config off — the caller (library.ts) uses this to show
   * an explanatory toast, since it's a real, potentially surprising side
   * effect of what otherwise looks like a plain file swap. */
  async replaceSongFile(songId: string, file: File): Promise<{ clickWasCleared: boolean }> {
    return this.enqueue(async () => {
      const song = this.state.songs.find((s) => s.id === songId);
      if (!song) return { clickWasCleared: false };
      const inspection = await inspectAudioFile(file);
      await database.putAudioBlob({
        songId,
        blob: file,
        mimeType: file.type || (inspection.fileFormat === 'mp3' ? 'audio/mpeg' : 'audio/wav'),
      });
      // MP3 can't seek precisely enough for a synthetic click to stay in
      // sync (see the Song Library's own disabled "Add Custom Click" tab
      // for a fresh MP3 import — same reasoning, applied here so a song
      // that HAD a click configured against its old WAV audio doesn't keep
      // running with a now-unreliable setup that's also unreachable in the
      // UI once the tab disables itself for the new MP3 file). Only
      // clearing when the new format is genuinely MP3 and the song
      // actually had something to clear — no-op (and no toast) for every
      // other replace.
      const clickWasCleared = inspection.fileFormat === 'mp3' && song.syntheticClickEnabled;
      const updated: SongRecord = {
        ...song,
        originalFileName: file.name,
        fileFormat: inspection.fileFormat,
        durationSeconds: inspection.durationSeconds,
        channelCount: inspection.channelCount,
        fileSizeBytes: file.size,
        status: inspection.status,
        statusMessage: inspection.statusMessage,
        updatedAt: Date.now(),
        ...(clickWasCleared
          ? {
              syntheticClickEnabled: false,
              firstBeatTimeSeconds: null,
              beatOneReferenceSeconds: null,
              // A beat-count lead-in (e.g. "8ct") only makes sense with a
              // click to count against — falls back to the plain-seconds
              // default rather than leaving markerLeadInSeconds at whatever
              // odd value beats*secondsPerBeat happened to compute to
              // against the OLD (now-gone) click's BPM.
              markerLeadInBeats: null,
              markerLeadInSeconds: DEFAULT_MARKER_LEAD_IN_SECONDS,
            }
          : {}),
      };
      await database.putSong(updated);
      this.state = { ...this.state, songs: this.state.songs.map((s) => (s.id === songId ? updated : s)) };
      this.notify();
      return { clickWasCleared };
    });
  }

  async deleteSong(songId: string): Promise<void> {
    return this.enqueue(async () => {
      await database.deleteSong(songId);
      const setlists = this.state.setlists.map((setlist) => ({
        ...setlist,
        entries: setlist.entries.filter((e) => e.songId !== songId),
        updatedAt: Date.now(),
      }));
      for (const setlist of setlists) await database.putSetlist(setlist);
      this.state = {
        ...this.state,
        songs: this.state.songs.filter((s) => s.id !== songId),
        setlists,
      };
      this.notify();
    });
  }

  // ---- Setlists ----

  async createSetlist(name: string, mode: SetlistMode = 'rehearsal'): Promise<SetlistRecord> {
    return this.enqueue(async () => {
      const now = Date.now();
      const setlist: SetlistRecord = {
        id: generateId(),
        name,
        createdAt: now,
        updatedAt: now,
        entries: [],
        mode,
        routing: DEFAULT_REHEARSAL_ROUTING,
        countInOverride: null,
      };
      await database.putSetlist(setlist);
      this.state = { ...this.state, setlists: [...this.state.setlists, setlist] };
      this.notify();
      return setlist;
    });
  }

  async renameSetlist(setlistId: string, newName: string): Promise<void> {
    return this.enqueue(() => this.mutateSetlistCore(setlistId, (s) => ({ ...s, name: newName })));
  }

  async setSetlistMode(setlistId: string, mode: SetlistMode): Promise<void> {
    return this.enqueue(() =>
      this.mutateSetlistCore(setlistId, (s) => {
        // Rehearsal and Live each only ever show/edit their OWN routing
        // family in the Setlists UI (see setlists.ts's own routingRow) — a
        // routing value left over from the other mode would otherwise sit
        // there unselected in whichever <select> just became visible,
        // silently stale until someone happens to touch it. Switching INTO
        // Live picks its safer default (Tracks Left, Click Right); switching
        // INTO Rehearsal restores the full-stereo default rather than
        // guessing which side the old Live split should map to.
        if (mode === s.mode) return { ...s, mode };
        const routing: SetlistRouting =
          mode === 'live' ? (s.routing === 'tracks-left-click-right' || s.routing === 'tracks-right-click-left' ? s.routing : 'tracks-left-click-right') : DEFAULT_REHEARSAL_ROUTING;
        return { ...s, mode, routing };
      }),
    );
  }

  async setSetlistRouting(setlistId: string, routing: SetlistRouting): Promise<void> {
    return this.enqueue(() => this.mutateSetlistCore(setlistId, (s) => ({ ...s, routing })));
  }

  async setSetlistCountInOverride(setlistId: string, countInOverride: SetlistCountInOverride): Promise<void> {
    return this.enqueue(() => this.mutateSetlistCore(setlistId, (s) => ({ ...s, countInOverride })));
  }

  async duplicateSetlist(setlistId: string): Promise<SetlistRecord | null> {
    return this.enqueue(async () => {
      const original = this.state.setlists.find((s) => s.id === setlistId);
      if (!original) return null;
      const now = Date.now();
      const copy: SetlistRecord = {
        id: generateId(),
        name: `${original.name} (Copy)`,
        createdAt: now,
        updatedAt: now,
        entries: [...original.entries],
        mode: original.mode,
        routing: original.routing,
        countInOverride: original.countInOverride,
      };
      await database.putSetlist(copy);
      this.state = { ...this.state, setlists: [...this.state.setlists, copy] };
      this.notify();
      return copy;
    });
  }

  async deleteSetlist(setlistId: string): Promise<void> {
    return this.enqueue(async () => {
      await database.deleteSetlist(setlistId);
      this.state = { ...this.state, setlists: this.state.setlists.filter((s) => s.id !== setlistId) };
      if (this.state.settings.activeSetlistId === setlistId) {
        await this.setActiveSetlistCore(null);
      }
      this.notify();
    });
  }

  async addSongToSetlist(setlistId: string, songId: string): Promise<void> {
    return this.enqueue(() => this.mutateSetlistCore(setlistId, (s) => ({ ...s, entries: [...s.entries, { songId }] })));
  }

  async removeSongFromSetlist(setlistId: string, entryIndex: number): Promise<void> {
    return this.enqueue(() =>
      this.mutateSetlistCore(setlistId, (s) => ({
        ...s,
        entries: s.entries.filter((_, i) => i !== entryIndex),
      })),
    );
  }

  async moveSetlistEntry(setlistId: string, fromIndex: number, toIndex: number): Promise<void> {
    return this.enqueue(() =>
      this.mutateSetlistCore(setlistId, (s) => {
        const entries = [...s.entries];
        if (toIndex < 0 || toIndex >= entries.length) return s;
        const [moved] = entries.splice(fromIndex, 1);
        entries.splice(toIndex, 0, moved);
        return { ...s, entries };
      }),
    );
  }

  /** Not queued directly — see updateSongCore's comment; same reasoning. */
  private async mutateSetlistCore(setlistId: string, mutate: (s: SetlistRecord) => SetlistRecord): Promise<void> {
    const setlist = this.state.setlists.find((s) => s.id === setlistId);
    if (!setlist) return;
    const updated = { ...mutate(setlist), updatedAt: Date.now() };
    await database.putSetlist(updated);
    this.state = { ...this.state, setlists: this.state.setlists.map((s) => (s.id === setlistId ? updated : s)) };
    this.notify();
  }

  // ---- Settings ----

  async setActiveSetlist(setlistId: string | null): Promise<void> {
    return this.enqueue(() => this.setActiveSetlistCore(setlistId));
  }

  /** Not queued directly — called both as a public entry point (wrapped
   * above) and from inside `deleteSetlist`'s already-queued execution. */
  private async setActiveSetlistCore(setlistId: string | null): Promise<void> {
    await database.setSetting('activeSetlistId', setlistId);
    await database.setSetting('activeEntryIndex', 0);
    this.state = {
      ...this.state,
      settings: { ...this.state.settings, activeSetlistId: setlistId, activeEntryIndex: 0 },
    };
    this.notify();
  }

  async setActiveEntryIndex(index: number): Promise<void> {
    return this.enqueue(async () => {
      await database.setSetting('activeEntryIndex', index);
      this.state = { ...this.state, settings: { ...this.state.settings, activeEntryIndex: index } };
      this.notify();
    });
  }

  /** Adds a user-defined note tab (see CustomNoteTab's own comment) —
   * global, immediately available on every song's marker editor and the
   * Player's tab picker, not something added per-song. Returns the created
   * tab (with its generated id) so a caller can switch straight to it. */
  async addCustomNoteTab(label: string): Promise<CustomNoteTab> {
    return this.enqueue(async () => {
      const tab: CustomNoteTab = { id: generateId(), label: label.trim() };
      const customNoteTabs = [...this.state.settings.customNoteTabs, tab];
      await database.setSetting('customNoteTabs', customNoteTabs);
      this.state = { ...this.state, settings: { ...this.state.settings, customNoteTabs } };
      this.notify();
      return tab;
    });
  }

  /** Renaming keeps the tab's id (and therefore every marker's existing
   * content under it) intact — only the display label changes, unlike
   * delete-and-recreate which always mints a fresh id. */
  async renameCustomNoteTab(id: string, label: string): Promise<void> {
    return this.enqueue(async () => {
      const trimmed = label.trim();
      if (!trimmed) return;
      const customNoteTabs = this.state.settings.customNoteTabs.map((tab) => (tab.id === id ? { ...tab, label: trimmed } : tab));
      await database.setSetting('customNoteTabs', customNoteTabs);
      this.state = { ...this.state, settings: { ...this.state.settings, customNoteTabs } };
      this.notify();
    });
  }

  /** Removes a custom tab from the picker everywhere — does NOT touch any
   * marker's existing `notes`/`noteFormats`/`noteSheets` entry under this
   * id (see CustomNoteTab's own comment on why that content is left inert
   * rather than swept out of every song). */
  async removeCustomNoteTab(id: string): Promise<void> {
    return this.enqueue(async () => {
      const customNoteTabs = this.state.settings.customNoteTabs.filter((tab) => tab.id !== id);
      await database.setSetting('customNoteTabs', customNoteTabs);
      this.state = { ...this.state, settings: { ...this.state.settings, customNoteTabs } };
      this.notify();
    });
  }

  /** Sets a SONG's lead-in duration (per-song, not global — see
   * SongRecord.markerLeadInSeconds's own comment). `beats` is null for a
   * plain seconds choice (the only option for a song with no known BPM);
   * pass the beat-count when the value was chosen from the beat-count
   * picker (synthetic-click songs only) so the countdown knows to
   * display/tick in beats rather than seconds. `seconds` is always the
   * actual value used for seek/countdown math regardless of which unit it
   * was chosen in — the beat-count picker computes it from THIS song's own
   * BPM at selection time and passes it here alongside the beat count,
   * rather than the countdown recomputing seconds-from-beats itself every
   * tick against a BPM that could differ if edited later. */
  async setSongMarkerLeadIn(songId: string, seconds: number, beats: number | null): Promise<void> {
    return this.enqueue(() => this.updateSongCore(songId, { markerLeadInSeconds: seconds, markerLeadInBeats: beats }));
  }

  // ---- Backup / restore ----

  exportBackup(appVersion: string): BackupFileV1 {
    return {
      formatVersion: 1,
      exportedAt: Date.now(),
      appVersion,
      songs: this.state.songs.map(({ status: _status, statusMessage: _statusMessage, ...rest }) => rest),
      setlists: this.state.setlists,
      bands: this.state.bands,
    };
  }

  /** Shared field-backfill for a song coming back from ANY backup format
   * (plain JSON-only, or the full zip with audio) — every field a backup
   * from an older app version might be missing gets the same default
   * load()'s own backfill would apply, so what's written to IndexedDB is
   * correct immediately rather than only after the next reload. Callers
   * supply the status/statusMessage themselves, since that's the one part
   * that genuinely differs: JSON-only import has no audio to inspect
   * (always 'missing'), while the full zip import re-inspects the restored
   * audio blob just like a fresh import would. */
  private backfillRestoredSong(song: BackupFileV1['songs'][number], status: SongStatus, statusMessage: string | null): SongRecord {
    return {
      ...song,
      status,
      statusMessage,
      bandId: song.bandId ?? null,
      markers: song.markers.map((m) => backfillMarker(m)),
      syntheticClickEnabled: song.syntheticClickEnabled ?? false,
      syntheticClickOffsetSeconds: song.syntheticClickOffsetSeconds ?? 0,
      firstBeatTimeSeconds: song.firstBeatTimeSeconds ?? legacyPickupBeatsToFirstBeatTime(song as SongRecord),
      beatOneReferenceSeconds: song.beatOneReferenceSeconds ?? null,
      markerLeadInSeconds: song.markerLeadInSeconds ?? DEFAULT_MARKER_LEAD_IN_SECONDS,
      markerLeadInBeats: song.markerLeadInBeats ?? null,
    };
  }

  /** The full-setlist export a bandmate can just download and import —
   * unlike exportBackup's plain JSON (settings/markers/setlist structure
   * only, explicitly NOT including audio — see its own UI copy), this
   * bundles the same manifest PLUS every song's actual audio blob into one
   * .zip, so importing it on another device needs no separate "find and
   * re-import each WAV" step at all. Archive layout:
   *   backup.json         — the same BackupFileV1 shape exportBackup produces
   *   audio/<songId>.bin  — each song's raw audio bytes, keyed by id (not
   *                         original filename, which isn't guaranteed unique
   *                         or even present — see SongRecord.originalFileName's
   *                         own doc comment) — extensionless .bin rather than
   *                         guessing .wav/.mp3, since the real format is
   *                         already recorded in the manifest and re-derived
   *                         via inspectAudioFile on import regardless.
   * A song with no audio loaded yet on THIS device (a pre-existing
   * 'missing' status) simply has no matching audio/ entry — importFullSetlist
   * handles that the same way importBackup's own JSON-only path does.
   *
   * `setlistId`, when given, scopes the archive to just that one setlist
   * (and only the songs it actually references) instead of the whole
   * library — the per-row "Export .zip" action in the Setlists list, as
   * opposed to the page-level Import/Export panel's all-setlists export. */
  async exportFullSetlistZip(appVersion: string, setlistId?: string): Promise<Blob> {
    const backup = this.exportBackup(appVersion);
    const scopedSetlists = setlistId ? backup.setlists.filter((s) => s.id === setlistId) : backup.setlists;
    const referencedSongIds = new Set(scopedSetlists.flatMap((s) => s.entries.map((e) => e.songId)));
    const scopedSongs = setlistId ? backup.songs.filter((song) => referencedSongIds.has(song.id)) : backup.songs;
    const scopedBackup: BackupFileV1 = { ...backup, setlists: scopedSetlists, songs: scopedSongs };
    const entries: { path: string; data: Uint8Array }[] = [
      { path: 'backup.json', data: new TextEncoder().encode(JSON.stringify(scopedBackup, null, 2)) },
    ];
    for (const song of scopedSongs) {
      const audio = await database.getAudioBlob(song.id);
      if (!audio) continue;
      entries.push({ path: `audio/${song.id}.bin`, data: new Uint8Array(await audio.blob.arrayBuffer()) });
    }
    return createZip(entries);
  }

  async importBackup(backup: BackupFileV1): Promise<void> {
    return this.enqueue(async () => {
      for (const band of backup.bands ?? []) {
        await database.putBand(band);
      }
      for (const song of backup.songs) {
        const restored = this.backfillRestoredSong(song, 'missing', 'Audio not included in backup file. Re-import the WAV file.');
        await database.putSong(restored);
      }
      for (const setlist of backup.setlists) {
        await database.putSetlist(setlist);
      }
      await this.load();
    });
  }

  /** Full setlist import: same manifest restore as importBackup, but each
   * song's ACTUAL audio blob travels alongside it (see exportFullSetlistZip
   * for the archive layout this reads) — a bandmate can open one file and
   * get every song ready to play immediately, with no separate "re-import
   * the WAV for each song" step. `audioBySongId` is missing an entry for
   * any song the exporting device itself hadn't loaded audio for yet (a
   * pre-existing 'missing' song) — that song is restored the same way
   * importBackup's own JSON-only path handles it, rather than failing the
   * whole import over one incomplete entry. */
  async importFullSetlist(backup: BackupFileV1, audioBySongId: Map<string, Blob>): Promise<void> {
    return this.enqueue(async () => {
      for (const band of backup.bands ?? []) {
        await database.putBand(band);
      }
      for (const song of backup.songs) {
        const audio = audioBySongId.get(song.id);
        if (!audio) {
          // A metronome-mode song never has audio at all, by design (see
          // createMetronomeSong) — its own 'ok' status must survive the
          // restore unchanged, not get overwritten with a "please re-import
          // the file" message that doesn't apply to it. Every OTHER mode
          // missing its audio entry genuinely does mean "the exporting
          // device hadn't loaded this file yet," so 'missing' is correct there.
          const restored =
            song.mode === 'metronome'
              ? this.backfillRestoredSong(song, 'ok', null)
              : this.backfillRestoredSong(song, 'missing', 'Audio not included in this export. Re-import the file.');
          await database.putSong(restored);
          continue;
        }
        const inspection = await inspectAudioFile(audio);
        const restored = this.backfillRestoredSong(
          { ...song, fileFormat: inspection.fileFormat, durationSeconds: inspection.durationSeconds, channelCount: inspection.channelCount },
          inspection.status,
          inspection.statusMessage,
        );
        await database.putSong(restored);
        await database.putAudioBlob({
          songId: song.id,
          blob: audio,
          mimeType: audio.type || (inspection.fileFormat === 'mp3' ? 'audio/mpeg' : 'audio/wav'),
        });
      }
      for (const setlist of backup.setlists) {
        await database.putSetlist(setlist);
      }
      await this.load();
    });
  }

  async clearAllData(): Promise<void> {
    return this.enqueue(async () => {
      await database.clearAllData();
      this.state = { songs: [], setlists: [], bands: [], settings: DEFAULT_SETTINGS, loaded: true };
      this.notify();
    });
  }

  // ---- Bands ----

  async createBand(name: string): Promise<BandRecord> {
    return this.enqueue(async () => {
      const band: BandRecord = { id: generateId(), name, createdAt: Date.now() };
      await database.putBand(band);
      this.state = { ...this.state, bands: [...this.state.bands, band] };
      this.notify();
      return band;
    });
  }

  async renameBand(bandId: string, newName: string): Promise<void> {
    return this.enqueue(async () => {
      const band = this.state.bands.find((b) => b.id === bandId);
      if (!band) return;
      const updated: BandRecord = { ...band, name: newName };
      await database.putBand(updated);
      this.state = { ...this.state, bands: this.state.bands.map((b) => (b.id === bandId ? updated : b)) };
      this.notify();
    });
  }

  /** Deleting a band never deletes its songs — they fall back to
   * Unassigned (`bandId: null`), the same place a song with no band ever
   * chosen already lives, rather than losing audio/markers/setlist
   * membership just because their organizing tag went away. */
  async deleteBand(bandId: string): Promise<void> {
    return this.enqueue(async () => {
      await database.deleteBand(bandId);
      const affectedSongs = this.state.songs.filter((s) => s.bandId === bandId);
      for (const song of affectedSongs) {
        await database.putSong({ ...song, bandId: null });
      }
      this.state = {
        ...this.state,
        bands: this.state.bands.filter((b) => b.id !== bandId),
        songs: this.state.songs.map((s) => (s.bandId === bandId ? { ...s, bandId: null } : s)),
      };
      this.notify();
    });
  }
}

export const appStore = new AppStore();

/** Parses a .zip produced by exportFullSetlistZip back into its manifest and
 * a lookup of song id -> audio Blob, ready to hand to
 * appStore.importFullSetlist. Kept as a standalone function (not an
 * AppStore method) since it doesn't touch app state at all — pure parsing,
 * same reasoning as detectAudioFormat/inspectWavFile living outside the
 * store. Throws if backup.json is missing or unparsable; silently ignores
 * any zip entry that isn't backup.json or under audio/, rather than
 * failing the whole import over an unrelated stray file. */
export async function parseFullSetlistZip(file: Blob): Promise<{ backup: BackupFileV1; audioBySongId: Map<string, Blob> }> {
  const entries = await readZip(file);
  const manifestEntry = entries.find((e) => e.path === 'backup.json');
  if (!manifestEntry) throw new Error('This file is missing its backup.json manifest — not a recognized full-setlist export.');

  const backup = JSON.parse(new TextDecoder().decode(manifestEntry.data)) as BackupFileV1;
  if (backup.formatVersion !== 1 || !Array.isArray(backup.songs) || !Array.isArray(backup.setlists)) {
    throw new Error('This file is not a recognized backup format.');
  }

  const audioBySongId = new Map<string, Blob>();
  for (const entry of entries) {
    const match = entry.path.match(/^audio\/(.+)\.bin$/);
    if (!match) continue;
    audioBySongId.set(match[1], new Blob([entry.data]));
  }

  return { backup, audioBySongId };
}
